/*
 * Copyright (c) 2011 Macrofocus GmbH. All Rights Reserved.
 */
package org.mkui.colormapping

import com.macrofocus.common.collection.CollectionFactory.copyOnWriteArrayList
import com.macrofocus.common.collection.WeakReference

/**
 * This class provides a skeletal implementation of the Colormapping interface to minimize the effort required to implement
 * this interface.
 */
abstract class AbstractColorMapping<E, C> : ColorMapping<E, C> {
    private val listeners: MutableList<ColorMappingListener<E, C>>

    override fun addColormappingListener(listener: ColorMappingListener<E, C>) {
        listeners.add(listener)
    }

    override fun addWeakColormappingListener(listener: ColorMappingListener<E, C>) {
        listeners.add(WeakColorMappingListener(listener))
    }

    override fun removeColormappingListener(listener: ColorMappingListener<E, C>) {
        if (listener is WeakColorMappingListener) {
            val removed = listeners.remove(listener)
        } else {
            var toRemove: ColorMappingListener<E, C>? = null
            for (filterListener in listeners) {
                var comparable: ColorMappingListener<E, C>?
                comparable = if (filterListener is WeakColorMappingListener) {
                    filterListener.reference
                } else {
                    filterListener
                }
                if (listener == comparable) {
                    toRemove = filterListener
                }
            }
            if (toRemove != null) {
                val removed = listeners.remove(toRemove)
            }
        }
    }

    override fun removeColormappingListeners() {
        listeners.clear()
    }

    protected fun notifyColormappingChanged() {
        for (listener in listeners) {
            listener.colorMappingChanged()
        }
    }

    private inner class WeakColorMappingListener(listener: ColorMappingListener<E, C>) : ColorMappingListener<E, C> {
        private val l_ref: WeakReference<ColorMappingListener<E, C>>
        override fun colorMappingChanged() {
            val l: ColorMappingListener<E, C>? = reference
            if (l != null) {
                l.colorMappingChanged()
            } else {
                removeColormappingListener(this as ColorMappingListener<E, C>)
            }
        }

        val reference: ColorMappingListener<E, C>?
            get() = l_ref.get()

        init {
            l_ref = WeakReference(listener)
        }
    }

    init {
        listeners = copyOnWriteArrayList()
    }
}